import React, { useEffect, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, ArrowLeft, Download, Printer, Mail } from "lucide-react";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import api, { formatApiErrorDetail, inr } from "./api";
import { useAuth } from "./AuthContext";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../components/ui/dialog";

const MONTHS = ["January", "February", "March", "April", "May", "June", "July",
  "August", "September", "October", "November", "December"];

const EARN_LABELS = {
  basic: "Basic", hra: "HRA", conveyance: "Conveyance", special_allowance: "Special Allowance",
  medical_allowance: "Medical Allowance", bonus: "Bonus", incentive: "Incentive",
  overtime: "Overtime", reimbursement: "Reimbursement",
};
const DED_LABELS = {
  employee_pf: "Provident Fund (PF)", esi: "ESI", professional_tax: "Professional Tax",
  tds: "TDS", lwp: "Leave Without Pay (LOP)", advances: "Advances", other_deductions: "Other Deductions",
};

export default function SalarySlip() {
  const { id } = useParams();
  const { user } = useAuth();
  const slipRef = useRef(null);
  const [slip, setSlip] = useState(null);
  const [emailOpen, setEmailOpen] = useState(false);
  const [emailTo, setEmailTo] = useState("");
  const [sending, setSending] = useState(false);
  const canEmail = ["super_admin", "hr_admin", "finance"].includes(user?.role);

  useEffect(() => {
    api.get(`/hr/payroll/${id}`).then((r) => {
      setSlip(r.data);
    }).catch(() => toast.error("Failed to load salary slip"));
  }, [id]);

  const downloadPdf = async () => {
    if (!slipRef.current) return;
    toast.info("Generating PDF...");
    try {
      const canvas = await html2canvas(slipRef.current, { scale: 2, backgroundColor: "#ffffff" });
      const img = canvas.toDataURL("image/png");
      const pdf = new jsPDF("p", "mm", "a4");
      const w = pdf.internal.pageSize.getWidth();
      const h = (canvas.height * w) / canvas.width;
      pdf.addImage(img, "PNG", 0, 0, w, h);
      pdf.save(`SalarySlip_${slip.employee_id}_${MONTHS[slip.month - 1]}_${slip.year}.pdf`);
    } catch (e) {
      toast.error("Could not generate PDF");
    }
  };

  const sendEmail = async () => {
    setSending(true);
    try {
      const { data } = await api.post(`/hr/payroll/${id}/email`, { email: emailTo || null });
      toast.success(data.message);
      setEmailOpen(false);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSending(false);
    }
  };

  if (!slip) return <div className="p-12 flex justify-center"><Loader2 className="animate-spin" style={{ color: "#00F0FF" }} /></div>;

  const { company: co, employee_snapshot: snap, computed: c } = slip;
  const payDate = (slip.created_at || "").slice(0, 10);
  const earnings = Object.entries(c.earnings).filter(([, v]) => v);
  const deductions = Object.entries(c.deductions).filter(([, v]) => v);

  return (
    <div data-testid="salary-slip-page">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6 no-print">
        <Link to="/portal/payroll" className="inline-flex items-center gap-2 mono-label hover:text-cyan" style={{ color: "#64748B" }}>
          <ArrowLeft size={14} /> Back to payroll
        </Link>
        <div className="flex gap-3">
          <button onClick={downloadPdf} data-testid="download-pdf-btn"
            className="inline-flex items-center gap-2 bg-[#0066FF] hover:bg-[#00F0FF] text-white hover:text-[#060A14] px-5 py-3 mono-label transition-colors">
            <Download size={16} /> Download PDF
          </button>
          <button onClick={() => window.print()} data-testid="print-btn"
            className="inline-flex items-center gap-2 border border-white/15 text-white px-5 py-3 mono-label hover:border-white/40 transition-colors">
            <Printer size={16} /> Print
          </button>
          {canEmail && (
            <button onClick={() => { setEmailTo(""); setEmailOpen(true); }} data-testid="email-slip-btn"
              className="inline-flex items-center gap-2 border border-white/15 text-white px-5 py-3 mono-label hover:border-white/40 transition-colors">
              <Mail size={16} /> Email
            </button>
          )}
        </div>
      </div>

      {/* Printable slip */}
      <div className="flex justify-center">
        <div id="salary-slip" ref={slipRef} className="bg-white w-full max-w-[720px] text-[#0f172a]" data-testid="slip-content">
          <div style={{ background: "#060A14" }} className="text-white px-7 py-5 flex items-center justify-between">
            <div>
              <div className="font-display font-black text-2xl" style={{ color: "#00F0FF" }}>A3NETWORKS</div>
              <div className="text-[13px] font-bold mt-1">{co.name}</div>
              <div className="text-[11px] mt-1" style={{ color: "#94A3B8" }}>{co.address}</div>
              <div className="text-[11px]" style={{ color: "#94A3B8" }}>GSTIN: {co.gst}</div>
            </div>
          </div>

          <div className="px-7 py-2.5 font-bold text-sm" style={{ background: "#EEF2F9" }}>
            Salary Slip — {MONTHS[slip.month - 1]} {slip.year}
          </div>

          <div className="grid grid-cols-2 gap-x-8 px-7 py-4 text-[13px]">
            <div className="space-y-1">
              <div><span className="text-slate-500">Employee: </span><b>{snap.full_name}</b></div>
              <div><span className="text-slate-500">Employee Code: </span>{snap.employee_code || "—"}</div>
              <div><span className="text-slate-500">Designation: </span>{snap.designation || "—"}</div>
              <div><span className="text-slate-500">Department: </span>{snap.department || "—"}</div>
            </div>
            <div className="space-y-1">
              <div><span className="text-slate-500">Date of Joining: </span>{snap.date_of_joining || "—"}</div>
              <div><span className="text-slate-500">Pay Date: </span>{payDate || "—"}</div>
              <div><span className="text-slate-500">PAN: </span>{snap.pan || "—"}</div>
              <div><span className="text-slate-500">UAN: </span>{snap.uan || "—"}</div>
              <div><span className="text-slate-500">Bank A/C: </span>{snap.bank_account || "—"}</div>
              <div><span className="text-slate-500">Paid Days: </span>{slip.inputs?.paid_days} / {c.total_days}</div>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-6 px-7 pb-4">
            <div>
              <div className="font-bold text-sm mb-2" style={{ color: "#0066FF" }}>Earnings</div>
              <table className="w-full text-[13px] border-collapse">
                <tbody>
                  {earnings.map(([k, v]) => (
                    <tr key={k}><td className="py-1.5 border-b border-slate-100">{EARN_LABELS[k] || k}</td><td className="py-1.5 border-b border-slate-100 text-right">{inr(v)}</td></tr>
                  ))}
                  <tr className="font-bold"><td className="py-2">Gross Earnings</td><td className="py-2 text-right">{inr(c.gross_earnings)}</td></tr>
                </tbody>
              </table>
            </div>
            <div>
              <div className="font-bold text-sm mb-2" style={{ color: "#0066FF" }}>Deductions</div>
              <table className="w-full text-[13px] border-collapse">
                <tbody>
                  {deductions.length ? deductions.map(([k, v]) => (
                    <tr key={k}><td className="py-1.5 border-b border-slate-100">{DED_LABELS[k] || k}</td><td className="py-1.5 border-b border-slate-100 text-right">{inr(v)}</td></tr>
                  )) : <tr><td className="py-1.5 text-slate-400">No deductions</td><td /></tr>}
                  <tr className="font-bold"><td className="py-2">Total Deductions</td><td className="py-2 text-right">{inr(c.total_deductions)}</td></tr>
                </tbody>
              </table>
            </div>
          </div>

          <div style={{ background: "#060A14" }} className="text-white px-7 py-4 flex items-center justify-between">
            <span className="font-bold">NET PAY</span>
            <span className="font-display font-black text-xl" style={{ color: "#00F0FF" }}>{inr(c.net_salary)}</span>
          </div>
          <div className="px-7 py-3 text-[12px] italic text-slate-600">{c.net_in_words}</div>
          <div className="px-7 py-2 text-[10px] text-slate-400 border-t border-slate-100">
            This is a system-generated salary slip and does not require a signature.
          </div>
        </div>
      </div>

      <Dialog open={emailOpen} onOpenChange={setEmailOpen}>
        <DialogContent className="bg-[#0C1322] border-white/10 text-white" data-testid="email-dialog">
          <DialogHeader><DialogTitle className="text-white">Email Salary Slip</DialogTitle></DialogHeader>
          <p className="text-sm" style={{ color: "#94A3B8" }}>
            Leave blank to send to the employee's registered email, or enter a specific address.
          </p>
          <input className="field" placeholder="employee@example.com (optional)"
            value={emailTo} onChange={(e) => setEmailTo(e.target.value)} data-testid="email-input" />
          <DialogFooter>
            <button onClick={sendEmail} disabled={sending} data-testid="send-email-btn"
              className="inline-flex items-center gap-2 bg-[#0066FF] hover:bg-[#00F0FF] text-white hover:text-[#060A14] px-6 py-3 mono-label transition-colors disabled:opacity-60">
              {sending ? <Loader2 size={16} className="animate-spin" /> : <><Mail size={16} /> Send</>}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
